// Component: TradingDataContext.jsx
// Purpose: Central trading-data authority, subscriptions, and selector hooks.
//---------------------------------------------------------------------------------------------------
//  ?                              TRADING DATA CONTEXT
//  @author         :  DeeTrade Team
//  @createdOn      :  04/10/2026
//  @description    :  Centralized trading data provider — WS subscriptions, API fetch, version-counter state
//---------------------------------------------------------------------------------------------------

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import moment from 'moment';
import { useAuth } from './AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import AlgoDashboardService from '../api/services/algoDashboard';
import OrdersService from '../api/services/orders';
import { mapEventTypeToStatus, deriveInstrumentId } from '../utils/tradingTransforms';
import { PositionsRegistry, derivePositionRowKey } from '../utils/positionsRegistry';
import { services } from '../services/api';
import { isExchangeOpenNow } from '../utils/exchangeHours';
import { isValidRuntimeHealth } from '../pages/AlgoTrading/utils/liveness';

// --> Context creation
export const TradingDataContext = createContext(undefined);

//--> Timer-lateness guard for the per-deployment runtime-health deadline: a deadline callback that
//*   runs this much later than it was due means the browser throttled/slept the timer (laptop sleep,
//*   background tab), NOT that the worker went silent — it re-arms instead of declaring offline.
//*   (This is not a worker-freshness threshold; the window itself is worker-authored `stale_after_ms`.)
const RUNTIME_DEADLINE_LATE_TIMER_GRACE_MS = 5000;

//--> One derivation rule for every chart-surface live instrument consumer.
export const liveInstrumentIdOf = (deployment) => (
  deployment?.resolved_instrument_id || deployment?.instrument_id || null
);

// =============================================================================
// Provider Component
// =============================================================================

export function TradingDataProvider({ children }) {
  const { user } = useAuth();
  const { isConnected, subscribeTopic } = useWebSocket({ page_id: 'trading-data' });

  // --> Version-counter state — the ONLY state that triggers re-renders
  const [versions, setVersions] = useState({
    orders: 0,
    positions: 0,
    performance: 0,
    deployments: 0,
    levelHistory: 0,
    deploymentTicks: 0,   // NEW — rAF-coalesced, at most one bump per paint
    liveExecution: 0,     // NEW — bumps on every strategy.execution.{deploymentId} payload
    reconcile: 0,         // R102.5 — bumps when reconcile snapshot merges into ordersRef
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(1);

  // --> Data refs — Maps stored in useRef, mutations NEVER trigger re-renders
  const ordersRef = useRef(new Map());
  //! positionsRef is a PositionsRegistry (NOT a raw Map) — the sole accessor for position rows.
  //! Direct .get/.set/.delete/.has are structurally unavailable (they fail loud). See positionsRegistry.js.
  const positionsRef = useRef(new PositionsRegistry());
  const performanceRef = useRef(new Map());
  const deploymentsRef = useRef(new Map());
  const ltpRef = useRef(new Map());
  //--> Per-strategy_instance_id array of trade_context snapshots from
  //*   strategy.trade_context.{sii} WS topic. Capped at LEVEL_HISTORY_MAX entries
  //*   per instance to bound memory on long-running deployments.
  const levelHistoryRef = useRef(new Map());

  // --> Subscription tracking refs
  const pnlListenersRef = useRef(new Map());
  const activeTickSubsRef = useRef(new Map());
  const activeLevelHistorySubsRef = useRef(new Map());
  //--> Eager pins for trade_context subscription keyed by sii → refcount.
  //*   Consumers (LiveAnalysisTab) pin on mount so the subscription exists
  //*   BEFORE the OMS fill event arrives. Without this, strategy-worker
  //*   publishes the entry-bar trade_context snapshot at bar N close, but the
  //*   positions-driven subscription path in syncLevelHistorySubscriptions
  //*   only registers AFTER the fill lands (post-broker-roundtrip in live,
  //*   post-tick-match in sim). The Redis → WS bridge drops the message when
  //*   no client is subscribed (see websocket-v2/handlers/subscription.py
  //*   broadcast_to_subscription — no buffering), so the band first becomes
  //*   visible on bar N+1 or later. Pinning removes the race at its source.
  const pinnedLevelHistorySiisRef = useRef(new Map());

  //--> Per-deployment_id latest strategy-execution payload.
  //*   Survives React strict-mode double-mount (module refs are per-instance
  //*   but live for the Provider's lifetime, not the consumer's). Kept on
  //*   Provider ref so consumer remounts don't reset state mid-session.
  const latestByDeploymentIdRef = useRef(new Map());
  // key: deploymentId (string)
  // value: { nodeResults, lastEvalTime }

  //--> Per-deployment_id active subscription + consumer ref count.
  //*   Multiple consumers (e.g. LiveAnalysisTab + MonitoringTab sharing the
  //*   same deployment) share a single WS subscription. We increment the
  //*   ref count on useLiveExecution mount and only call unsub when the
  //*   last consumer unmounts.
  const activeExecutionSubsRef = useRef(new Map());
  // key: deploymentId → { unsub: () => void, count: number }
  const activePositionSubsRef = useRef(new Map());     // sii -> { unsub, count }
  const activePerformanceSubsRef = useRef(new Map());  // sii -> { unsub, count }
  //! 44-2 — per-sii monotonic generation counter. Bumped on every WS position.<sii> frame so a
  //!  late first-pin REST hydration (subscribePosition below) can detect a fresher WS frame landed
  //!  while the request was in flight and skip applying its (now-stale) snapshot.
  //! Entries are NEVER deleted on unsubscribe — the guard needs the generation to persist ACROSS an
  //!  unpin/re-pin cycle too (a WS frame that lands after unpin but before the next pin's hydration
  //!  resolves must still be able to out-rank it). See the subscription-identity guard below for the
  //!  companion half of this (REST-vs-REST, not just REST-vs-WS).
  const positionWsGenRef = useRef(new Map());          // sii -> integer generation
  //! 10502-3 — per-sii monotonic generation for performance close-frame refetches. Incremented on
  //!  every `performance.<sii>` frame; a refetch captures the value at send time and is dropped on
  //!  return if a newer frame (newer refetch) has since fired. Never deleted on unsubscribe so the
  //!  guard persists ACROSS an unpin/re-pin cycle (companion to the entry-identity check).
  const performanceRefreshGenRef = useRef(new Map());  // sii -> integer generation

  //--> Per-resolved_instrument_id tick state. Populated by handleTick in tick-subscription
  //*   effect. Read by useDeploymentLtp via deploymentsRef → resolved_instrument_id lookup.
  const deploymentTicksRef = useRef(new Map());
  // key: resolved_instrument_id (string)
  // value: { ltp, change, changePct, direction, lastTickAt, symbol, exchange,
  //          lastTickTime (epoch ms from exchange_time), tickType (e.g. 'SIMULATED'),
  //          exchange_time (raw ISO string), volume (number|null) }

  const previousCloseRef = useRef(new Map());
  // key: resolved_instrument_id → number (previous day close)

  const brokerAccountIdRef = useRef(null);

  //--> Reconcile snapshot ref — stores { orders: [...], as_of } from last postReconcile call.
  //*   Keyed snapshot; Orders.jsx reads this directly for OrderDetailDrawer sourcing (R104.6).
  const reconcileSnapshotRef = useRef(null);
  // --> Reconcile display state — surfaces error banner and as_of to consumers
  const [reconcileAsOf, setReconcileAsOf] = useState(null);
  const [reconcileError, setReconcileError] = useState(false);
  //--> Per-broker status list from consolidated response (R106.7) — [{broker, degraded, error}]
  const [brokerStatus, setBrokerStatus] = useState([]);
  //! R104.14 — structured degraded flags from consolidated response (three independent banners)
  const [omsDegraded, setOmsDegraded] = useState(false);
  //! D7/M1 — 4th INDEPENDENT flag: OMS positions leg unreachable. NEVER OR'd into omsDegraded.
  const [omsPositionsDegraded, setOmsPositionsDegraded] = useState(false);
  const [classificationDegraded, setClassificationDegraded] = useState(false);
  const [brokerDegraded, setBrokerDegraded] = useState(false);
  const [brokerOrdersAsOf, setBrokerOrdersAsOf] = useState(null);

  const activeDeploymentTickSubsRef = useRef(new Map());
  // key: resolved_instrument_id → unsubFn (ref-based dedup)

  //--> requestAnimationFrame handle for coalesced version bumps.
  const tickVersionRafRef = useRef(null);

  // --> Cleanup tracking for WS topic unsubscribers
  const userSubsRef = useRef([]);

  //--> Runtime-health heartbeat deadlines — ONE timeout per running deployment_id:
  //*   Map<deployment_id, { timeoutId, dueAt, staleAfterMs, connectionEpoch }>. Re-armed on every
  //*   REST seed / runtime_health push (heartbeat-timeout pattern), cleared on disconnect/unmount.
  const runtimeDeadlinesRef = useRef(new Map());
  const runtimeConnectionEpochRef = useRef(0);
  const isConnectedRef = useRef(isConnected);

  // =========================================================================
  // Version bumpers — increment only the specific counter
  // =========================================================================

  const bumpVersion = useCallback((key) => {
    setVersions((prev) => ({ ...prev, [key]: prev[key] + 1 }));
  }, []);

  // =========================================================================
  // Runtime-health heartbeat — ONE per-deployment deadline path (REST seed + WS push)
  // =========================================================================

  const clearRuntimeDeadline = useCallback((deploymentId) => {
    const rec = runtimeDeadlinesRef.current.get(deploymentId);
    if (rec) clearTimeout(rec.timeoutId);
    runtimeDeadlinesRef.current.delete(deploymentId);
  }, []);

  const clearAllRuntimeDeadlines = useCallback(() => {
    runtimeDeadlinesRef.current.forEach((rec) => clearTimeout(rec.timeoutId));
    runtimeDeadlinesRef.current.clear();
  }, []);

  //--> Arms exactly one deadline for `deploymentId` (clear-before-rearm). Fires at `dueAt`
  //*   (= lastHeard + the worker-authored stale_after_ms) and marks the worker offline unless
  //*   the socket dropped, the deadline was superseded, or the timer itself ran very late.
  const armRuntimeDeadline = useCallback((deploymentId, staleAfterMs, dueAt) => {
    clearRuntimeDeadline(deploymentId);
    const fire = () => {
      const rec = runtimeDeadlinesRef.current.get(deploymentId);
      if (!rec || rec.timeoutId !== timeoutId) return; // superseded
      runtimeDeadlinesRef.current.delete(deploymentId);
      //! Silence only counts while the WS is connected, in the same connection epoch.
      if (!isConnectedRef.current || rec.connectionEpoch !== runtimeConnectionEpochRef.current) return;
      const dep = deploymentsRef.current.get(deploymentId);
      if (!dep || dep.status !== 'running') return;
      const now = Date.now();
      if (now - rec.dueAt > RUNTIME_DEADLINE_LATE_TIMER_GRACE_MS) {
        //--> Late timer (sleep/throttling): grant one full worker-supplied window instead.
        armRuntimeDeadline(deploymentId, rec.staleAfterMs, now + rec.staleAfterMs);
        return;
      }
      deploymentsRef.current.set(deploymentId, { ...dep, runtime_offline: true });
      bumpVersion('deployments');
    };
    const timeoutId = setTimeout(fire, Math.max(0, dueAt - Date.now()));
    runtimeDeadlinesRef.current.set(deploymentId, {
      timeoutId,
      dueAt,
      staleAfterMs,
      connectionEpoch: runtimeConnectionEpochRef.current,
    });
  }, [bumpVersion, clearRuntimeDeadline]);

  //--> THE chokepoint for runtime health (REST seed and push both call it). Stores the raw blob
  //*   and offline flag on the held running deployment and re-arms its deadline. Returns false —
  //*   changing nothing — for an unknown/non-running deployment, an invalid blob (no finite positive
  //*   stale_after_ms) or a non-finite lastHeardMs, so no zero/NaN timeout is ever armed.
  //*   A seed whose lastHeard is already older than stale_after_ms reads offline immediately.
  const applyRuntimeHealth = useCallback((deploymentId, blob, lastHeardMs, { notify = false } = {}) => {
    const dep = deploymentsRef.current.get(deploymentId);
    if (!dep || dep.status !== 'running') return false;
    if (!isValidRuntimeHealth(blob) || !Number.isFinite(lastHeardMs)) return false;

    const dueAt = lastHeardMs + blob.stale_after_ms;
    const offline = blob.offline === true || dueAt <= Date.now();
    deploymentsRef.current.set(deploymentId, { ...dep, runtime_health: blob, runtime_offline: offline });

    if (offline || !isConnectedRef.current) {
      clearRuntimeDeadline(deploymentId);
    } else {
      armRuntimeDeadline(deploymentId, blob.stale_after_ms, dueAt);
    }
    if (notify) bumpVersion('deployments');
    return true;
  }, [armRuntimeDeadline, bumpVersion, clearRuntimeDeadline]);

  //--> Connection tracking: a (re)connect or disconnect starts a new epoch; a disconnect clears
  //*   every deadline and HOLDS the labels. The existing reconnect refetch below re-seeds them.
  //*   Declared BEFORE the init effect so the ref is current when the seed arrives.
  useEffect(() => {
    isConnectedRef.current = isConnected;
    runtimeConnectionEpochRef.current += 1;
    if (!isConnected) clearAllRuntimeDeadlines();
  }, [isConnected, clearAllRuntimeDeadlines]);

  // =========================================================================
  // Deployment-level tick subscription — live price ticker for every running deployment
  // =========================================================================

  //--> rAF-coalesced version bump. Paint-aligned, auto-paused on backgrounded
  //*   tabs (browser suppresses rAF). No stated latency window — effective
  //*   visible lag is one paint frame (≤16.7ms on 60 Hz).
  const scheduleTickVersionBump = useCallback(() => {
    if (tickVersionRafRef.current != null) return;
    tickVersionRafRef.current = requestAnimationFrame(() => {
      tickVersionRafRef.current = null;
      bumpVersion('deploymentTicks');
    });
  }, [bumpVersion]);

  const handleTick = useCallback((resolvedId, msg) => {
    //--> Rollover race guard — drop ticks for a resolved_id we no longer hold a
    //*   subscription for. Prevents a late-arriving old-contract tick (queued in
    //*   the WS decoder between unsub() and handler deregistration during a mid-
    //*   session F&O rollover) from polluting direction math for the new contract.
    if (!activeDeploymentTickSubsRef.current.has(resolvedId)) return;

    const tick = msg?.data || msg;
    const newLtp = tick.ltp ?? tick.last_price;
    //! Zero/absent LTP defense-in-depth — treat null/undefined AND <= 0 as
    //! "NO UPDATE" (a broker/aggregator gap can still leak a zero tick through
    //! to this surface). Never overwrite last-known ltp with a zero/bad value.
    if (newLtp == null || newLtp <= 0) return;

    const prev = deploymentTicksRef.current.get(resolvedId);
    const prevClose = previousCloseRef.current.get(resolvedId);

    const change = tick.change_value ?? tick.change ??
      (prevClose != null ? newLtp - prevClose : null);
    const changePct = tick.change_percent ??
      (prevClose != null && prevClose > 0 ? ((newLtp - prevClose) / prevClose) * 100 : null);

    const direction =
      prev?.ltp == null ? 'neutral' :
      newLtp > prev.ltp ? 'up' :
      newLtp < prev.ltp ? 'down' : (prev.direction ?? 'neutral');

    //--> Parse exchange_time (UTC ISO string from WS payload) → epoch ms.
    //*   Null fallback if field is absent (e.g. broker adapters that don't emit it yet).
    const rawExchangeTime = tick.exchange_time ?? null;
    const lastTickTime = rawExchangeTime
      ? moment.utc(rawExchangeTime).valueOf()
      : null;
    const tickType = tick.tick_type ?? null;

    //--> DL-5: market-depth fields merge-keep the previous entry's value when the
    //*   incoming frame's array is null/empty (or the scalar total is null/undefined).
    //*   A new populated frame fully replaces. Depth is retained, never blanked, by a
    //*   frame that simply doesn't carry it (fused_tick.* depth keys can be None on an
    //*   LTP-only frame — fusion_engine.py:2017-2021).
    const mergeDepthArray = (incoming, prevVal) =>
      Array.isArray(incoming) && incoming.length > 0 ? incoming : (prevVal ?? null);
    const mergeDepthScalar = (incoming, prevVal) =>
      incoming != null ? incoming : (prevVal ?? null);

    deploymentTicksRef.current.set(resolvedId, {
      ltp: newLtp,
      change,
      changePct,
      direction,
      lastTickAt: Date.now(),
      symbol: tick.symbol ?? prev?.symbol,
      exchange: tick.exchange ?? prev?.exchange,
      lastTickTime,
      tickType,
      exchange_time: rawExchangeTime,
      volume: tick.volume ?? null,
      bid_prices: mergeDepthArray(tick.bid_prices, prev?.bid_prices),
      ask_prices: mergeDepthArray(tick.ask_prices, prev?.ask_prices),
      bid_qtys: mergeDepthArray(tick.bid_qtys, prev?.bid_qtys),
      ask_qtys: mergeDepthArray(tick.ask_qtys, prev?.ask_qtys),
      bid_orders: mergeDepthArray(tick.bid_orders, prev?.bid_orders),
      ask_orders: mergeDepthArray(tick.ask_orders, prev?.ask_orders),
      total_buy_qty: mergeDepthScalar(tick.total_buy_qty, prev?.total_buy_qty),
      total_sell_qty: mergeDepthScalar(tick.total_sell_qty, prev?.total_sell_qty),
    });
    scheduleTickVersionBump();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  //! Empty deps intentional — handleTick closes over refs (deploymentTicksRef,
  //! previousCloseRef, activeDeploymentTickSubsRef) and the module-scoped
  //! scheduleTickVersionBump callback, all of which are stable across renders.
  //! Adding scheduleTickVersionBump to deps would cause handler reinstallation
  //! on every scheduleTickVersionBump identity change (none — it's useCallback
  //! with stable [bumpVersion] deps), but the eslint rule can't prove this.
  }, []);

  const syncDeploymentTickSubscriptions = useCallback(() => {
    //--> Build the set of resolved_instrument_ids needed right now.
    //*   V12B-11: carry each id's venue alongside, so the tick subscription can declare
    //*   which exchange's hours govern its silence to the delivery-stall watchdog.
    const needed = new Map(); // resolvedId -> exchange | undefined
    deploymentsRef.current.forEach((dep) => {
      const resolvedId = liveInstrumentIdOf(dep);
      if (dep.status === 'running' && resolvedId) {
        needed.set(resolvedId, dep.instrument_exchange);
      }
    });

    //--> Unsubscribe removed ids (deployment stopped or rolled over)
    activeDeploymentTickSubsRef.current.forEach((unsub, id) => {
      if (!needed.has(id)) {
        try { unsub(); } catch { /* already gone */ }
        activeDeploymentTickSubsRef.current.delete(id);
        //* Drop stale tick state + prev-close so a later re-subscribe starts fresh
        deploymentTicksRef.current.delete(id);
        previousCloseRef.current.delete(id);
      }
    });

    //--> Subscribe new ids (dedup via has())
    needed.forEach((exchange, id) => {
      if (!activeDeploymentTickSubsRef.current.has(id)) {
        const topic = `market.${id}.tick`;
        //! M2-2: HQ demand is requested UNCONDITIONALLY — no client-side tier check.
        //! The server is the single tier authority (streaming_orchestrator.py:1399-1460).
        const unsub = subscribeTopic(topic, (msg) => handleTick(id, msg), { exchange, HQ: true });
        activeDeploymentTickSubsRef.current.set(id, unsub);
      }
    });
  }, [subscribeTopic, handleTick]);

  // =========================================================================
  // Tick subscription management
  // =========================================================================

  /**
   * Diff open positions' instrument_ids vs current activeTickSubsRef.
   * Subscribe to new instruments, unsubscribe from stale ones.
   */
  const syncTickSubscriptions = useCallback(() => {
    //--> Gather all instrument_ids that have open positions
    //*   V12B-11: keyed to the position's venue so the tick subscription can declare it.
    const neededIds = new Map(); // instrument_id -> exchange | undefined
    positionsRef.current.values().forEach((pos) => {
      if (pos.instrument_id) {
        neededIds.set(pos.instrument_id, pos.exchange);
      }
    });

    //--> Unsubscribe from instruments with no remaining open position
    activeTickSubsRef.current.forEach((unsubFn, instrumentId) => {
      if (!neededIds.has(instrumentId)) {
        unsubFn();
        activeTickSubsRef.current.delete(instrumentId);
      }
    });

    //--> Subscribe to new instruments
    neededIds.forEach((exchange, instrumentId) => {
      if (!activeTickSubsRef.current.has(instrumentId)) {
        const topic = `market.${instrumentId}.tick`;
        const unsubFn = subscribeTopic(topic, (msg) => {
          const ltp = msg?.data?.ltp ?? msg?.ltp ?? msg?.data?.last_price;
          //! Zero/absent LTP defense-in-depth — null/undefined OR <= 0 is
          //! "NO UPDATE": never overwrite ltpRef, never fire P&L listeners.
          if (ltp != null && ltp > 0) {
            //! Update ltpRef — NO re-render triggered
            ltpRef.current.set(instrumentId, ltp);

            //--> Notify all P&L listeners for this instrument
            const listeners = pnlListenersRef.current.get(instrumentId);
            if (listeners) {
              listeners.forEach((cb) => cb(ltp));
            }
          }
          //! M2-2: HQ demand is requested UNCONDITIONALLY — no client-side tier check.
          //! The server is the single tier authority (streaming_orchestrator.py:1399-1460).
        }, { exchange, HQ: true });
        activeTickSubsRef.current.set(instrumentId, unsubFn);
      }
    });
  }, [subscribeTopic]);

  // =========================================================================
  // Trade-context subscription management — mirrors tick sync above.
  //*   One WS subscription per open-position strategy_instance_id. Messages
  //*   accumulate into levelHistoryRef[sii] so the live chart can replay the
  //*   per-bar SL/TP band exactly like backtest trades.
  // =========================================================================

  const LEVEL_HISTORY_MAX = 2000; // cap per-instance array to bound memory

  const syncLevelHistorySubscriptions = useCallback(() => {
    const neededIds = new Set();
    //! Iterate values() (registry, not raw Map) and key by pos.strategy_instance_id — the
    //! level-history topic is `strategy.trade_context.${sii}`, so the bare sii is required here,
    //! NOT the registry's full-identity map key.
    positionsRef.current.values().forEach((pos) => {
      //--> Only subscribe for positions with qty > 0 (still open)
      if ((pos.qty ?? pos.quantity ?? 0) !== 0 && pos.strategy_instance_id) {
        neededIds.add(pos.strategy_instance_id);
      }
    });
    //--> Union with eager pins so a mounted LiveAnalysisTab keeps the sub alive
    //*   even before any fill arrives. Refcount > 0 → sub stays.
    pinnedLevelHistorySiisRef.current.forEach((count, sii) => {
      if (count > 0) neededIds.add(sii);
    });

    //--> Unsubscribe from closed positions
    activeLevelHistorySubsRef.current.forEach((unsubFn, sii) => {
      if (!neededIds.has(sii)) {
        unsubFn();
        activeLevelHistorySubsRef.current.delete(sii);
        //* Keep the history buffer so a re-hover on the closed trade still renders
        //* its band. Clearing it would erase the chart overlay on close.
      }
    });

    //--> Subscribe for new open positions
    neededIds.forEach((sii) => {
      if (!activeLevelHistorySubsRef.current.has(sii)) {
        //--> NEW subscription — reset the buffer so a re-opened position with the same
        //*   strategy_instance_id doesn't carry stale snapshots from the previous trade
        //*   cycle. Without this, LiveAnalysisTab's buildLiveLevelHistory would pin
        //*   entry_time to a stale snapshot and the band would visually span a closed
        //*   trade's gap. Close→re-open is the common case for cycling strategies.
        levelHistoryRef.current.set(sii, []);
        const topic = `strategy.trade_context.${sii}`;
        const unsubFn = subscribeTopic(topic, (msg) => {
          const snapshot = msg?.data || msg;
          if (!snapshot || !snapshot.timestamp) return;

          //--> [DIAG] 2026-04-27 temporary — track exit-bar vs per-bar arrival
          //*   to diagnose the live "band stops one candle short" bug. Bar
          //*   boundaries end with :00 seconds; exit-bar snapshots use the
          //*   fill instant. Remove this once the gap is closed.
          const _diagTs = String(snapshot.timestamp);
          const _isBoundary = /T\d{2}:\d{2}:00(\.0+)?(\+|Z|$)/.test(_diagTs);
          // eslint-disable-next-line no-console
          console.log(
            `[DIAG] WS trade_context sii=${sii.slice(-12)} ts=${_diagTs} ` +
            `kind=${_isBoundary ? 'PER-BAR' : 'EXIT-BAR'} ` +
            `qty=${snapshot.position?.qty} side=${snapshot.position?.side} ` +
            `entry_time=${snapshot.position?.entry_time}`
          );

          const arr = levelHistoryRef.current.get(sii) || [];
          //! Dedup by (timestamp, lifecycle_id). Backend publishes a PRELIMINARY
          //!  entry-bar trade_context at bar N close (position.entry_price =
          //!  signal.entry_price, SL/TP NULL). When ORDER_FILLED arrives,
          //!  fill_event_consumer republishes the SAME bar_start timestamp with
          //!  the actual fill price + SL/TP computed from that fill. Without
          //!  dedup both snapshots stay in the buffer and TradeBandSeries draws
          //!  a doubled point at the entry candle (regression observed
          //!  2026-04-22).
          //!
          //!  CRITICAL — include lifecycle_id (trade-key) in the dedup. When a
          //!  TP fills at bar X and a NEW trade enters at the same bar X
          //!  (back-to-back trades, no FLAT period), without this gate the
          //!  new trade's entry-bar snapshot would OVERWRITE the closing
          //!  trade's exit-bar snapshot at ts=X, collapsing the closing
          //!  trade's band to its (X-1) right edge. With the gate, both
          //!  snapshots survive — buildLiveLevelHistory then segments them by
          //!  entry_time so each trade renders independently.
          //!  Fix landed 2026-05-03 alongside backend lifecycle_id propagation.
          //!  Snapshots from older sims may have lifecycle_id=null — falling
          //!  back to timestamp-only dedup preserves prior behaviour for
          //!  those (no regression on legacy data).
          const newLcId = snapshot.lifecycle_id ?? snapshot.position?.lifecycle_id ?? null;
          let replaceIdx = -1;
          for (let i = arr.length - 1; i >= 0; i -= 1) {
            const candidate = arr[i];
            if (candidate?.timestamp !== snapshot.timestamp) continue;
            const candLcId = candidate.lifecycle_id ?? candidate.position?.lifecycle_id ?? null;
            //--> Same timestamp: dedup ONLY when lifecycle_id matches OR either
            //*   side has no lifecycle_id (legacy / pre-fill snapshots).
            //*   Two distinct lifecycles at the same timestamp are kept apart.
            if (candLcId === newLcId || candLcId == null || newLcId == null) {
              replaceIdx = i;
              break;
            }
          }
          if (replaceIdx >= 0) {
            arr[replaceIdx] = snapshot;
          } else {
            arr.push(snapshot);
          }
          if (arr.length > LEVEL_HISTORY_MAX) arr.splice(0, arr.length - LEVEL_HISTORY_MAX);
          levelHistoryRef.current.set(sii, arr);
          bumpVersion('levelHistory');
        });
        activeLevelHistorySubsRef.current.set(sii, unsubFn);
      }
    });
  }, [subscribeTopic]);

  //--> Eager-pin API: ref-counted sii pins so a subscription can be held open
  //*   by a mounted LiveAnalysisTab before any fill arrives. Pair ensure/release
  //*   1:1 per consumer (mount/unmount). Safe to call repeatedly — refcounting
  //*   keeps the subscription alive while at least one consumer wants it.
  const ensureLevelHistorySubscription = useCallback((sii) => {
    if (!sii) return;
    const current = pinnedLevelHistorySiisRef.current.get(sii) || 0;
    pinnedLevelHistorySiisRef.current.set(sii, current + 1);
    syncLevelHistorySubscriptions();
  }, [syncLevelHistorySubscriptions]);

  const releaseLevelHistorySubscription = useCallback((sii) => {
    if (!sii) return;
    const current = pinnedLevelHistorySiisRef.current.get(sii) || 0;
    if (current <= 1) {
      pinnedLevelHistorySiisRef.current.delete(sii);
    } else {
      pinnedLevelHistorySiisRef.current.set(sii, current - 1);
    }
    syncLevelHistorySubscriptions();
  }, [syncLevelHistorySubscriptions]);

  // =========================================================================
  // P&L listener registration
  // =========================================================================

  const registerPnlListener = useCallback((instrumentId, callback) => {
    if (!pnlListenersRef.current.has(instrumentId)) {
      pnlListenersRef.current.set(instrumentId, new Set());
    }
    pnlListenersRef.current.get(instrumentId).add(callback);
  }, []);

  const unregisterPnlListener = useCallback((instrumentId, callback) => {
    const listeners = pnlListenersRef.current.get(instrumentId);
    if (listeners) {
      listeners.delete(callback);
      if (listeners.size === 0) {
        pnlListenersRef.current.delete(instrumentId);
      }
    }
  }, []);

  // =========================================================================
  // Live strategy-execution subscription — per-deployment ref-counted dedupe
  //*   Multiple consumers (LiveAnalysisTab + MonitoringTab on the same
  //*   deployment) share ONE WS subscription. Subscribe on first mount,
  //*   unsubscribe on last unmount. Survives strict-mode double-mount via
  //*   Provider-scoped refs (latestByDeploymentIdRef preserves last payload
  //*   across consumer remounts).
  // =========================================================================

  const subscribeLiveExecution = useCallback((deploymentId) => {
    if (!deploymentId) return;
    const existing = activeExecutionSubsRef.current.get(deploymentId);
    if (existing) {
      //--> Already subscribed — just bump the ref count.
      existing.count += 1;
      return;
    }
    const topic = `strategy.execution.${deploymentId}`;
    const unsub = subscribeTopic(topic, (message) => {
      const data = message?.d || message?.data || message;

      //--> Payload-level guard: enforce deployment_id match when available.
      if (data?.deployment_id && String(data.deployment_id) !== String(deploymentId)) {
        return;
      }

      const nodeResults = data?.node_results || {};
      const lastEvalTime = data?.timestamp || null;
      const barTimeframe = data?.bar_timeframe || null;
      //! Board #73 — top-level `engine_decision` key, omitted on a bar with no
      //!  triggered signal node (E5a convention). Read in the same message
      //!  handler as nodeResults so both always describe the same bar.
      const engineDecision = data?.engine_decision || null;
      //--> Retained replay (bridge sends the worker's last bar right after the subscribe ACK) can
      //*   land AFTER a live frame for a newer bar; an older bar never overwrites a newer one.
      //*   Same-bar re-delivery is applied (same payload, harmless). No timestamp → applied.
      const prev = latestByDeploymentIdRef.current.get(deploymentId);
      const prevMs = prev?.lastEvalTime ? Date.parse(prev.lastEvalTime) : NaN;
      const nextMs = lastEvalTime ? Date.parse(lastEvalTime) : NaN;
      if (Number.isFinite(prevMs) && Number.isFinite(nextMs) && nextMs < prevMs) {
        return;
      }
      latestByDeploymentIdRef.current.set(deploymentId, { nodeResults, lastEvalTime, barTimeframe, engineDecision });
      bumpVersion('liveExecution');
    });
    activeExecutionSubsRef.current.set(deploymentId, { unsub, count: 1 });
  }, [subscribeTopic, bumpVersion]);

  const unsubscribeLiveExecution = useCallback((deploymentId) => {
    if (!deploymentId) return;
    const entry = activeExecutionSubsRef.current.get(deploymentId);
    if (!entry) return;
    entry.count -= 1;
    if (entry.count <= 0) {
      try { entry.unsub(); } catch { /* already gone */ }
      activeExecutionSubsRef.current.delete(deploymentId);
      //* Keep latestByDeploymentIdRef entry so a consumer that remounts within
      //* the same Provider lifetime sees the last-known value while waiting
      //* for the next payload. Cleared on Provider unmount below.
    }
  }, []);

  //--> 44-2: ONE chokepoint for applying a `/algo/summary` response onto positionsRef/performanceRef.
  //*   Shared by the init effect (full clearLeg('ALGO') + performanceRef.clear() beforehand) AND the
  //*   first-pin hydration in subscribePosition below — the init loop this replaces is DELETED, not
  //*   duplicated (rubric row 5).
  //--> 10502-3: the SOLE writer of a family `performance` row into performanceRef — shared by
  //*   applyAlgoSummary (init + first-pin hydration) and the close-frame refetch in
  //*   subscribePerformance. The row is the OMS /algo/summary family aggregate, keyed by the
  //*   REQUESTED sii (not any resolved/rolled instrument id).
  //! F-2: every request that may write a family row takes a per-sid generation token BEFORE it is
  //!  sent (`takePerformanceToken`); `setFamilyPerformance` applies the row only while that token is
  //!  still current, so a response overtaken by a fresher request (close-frame refetch, later
  //!  init/pin) can never overwrite the fresher row. `token === undefined` = unconditional write.
  const takePerformanceToken = useCallback((sii) => {
    const gen = (performanceRefreshGenRef.current.get(sii) || 0) + 1;
    performanceRefreshGenRef.current.set(sii, gen);
    return gen;
  }, []);

  const setFamilyPerformance = useCallback((sii, performance, token) => {
    if (token !== undefined && (performanceRefreshGenRef.current.get(sii) || 0) !== token) return false;
    performanceRef.current.set(sii, {
      ...performance,
      strategy_instance_id: sii,
    });
    return true;
  }, []);

  //   `tokens` (optional Map<sid, gen>) = generation tokens taken before the request was sent.
  const applyAlgoSummary = useCallback((summaryData, tokens) => {
    Object.entries(summaryData || {}).forEach(([sid, data]) => {
      if (data && data.position) {
        const instrumentId = deriveInstrumentId(sid);
        positionsRef.current.upsert({
          ...data.position,
          strategy_instance_id: sid,
          instrument_id: instrumentId,
          leg: 'ALGO',
        });
      } else {
        //! Authoritative FLAT — /algo/summary omitting/nulling `position` means OMS has no open
        //! ALGO position for this sii. Delete any stale row rather than leaving a ghost (no-op
        //! at init, where clearLeg('ALGO') already emptied the leg first).
        positionsRef.current.deleteBySiiAndLeg(sid, 'ALGO');
      }
      if (data && data.performance) {
        setFamilyPerformance(sid, data.performance, tokens ? tokens.get(sid) : undefined);
      }
    });
  }, [setFamilyPerformance]);

  const subscribePosition = useCallback((sii) => {
    if (!sii) return;
    const existing = activePositionSubsRef.current.get(sii);
    if (existing) { existing.count += 1; return; }

    const topic = `position.${sii}`;
    const unsub = subscribeTopic(topic, (msg) => {
      const position = msg?.d || msg?.data || msg;
      if (!position) return;

      //! 44-2 — every WS frame for this sii advances its generation BEFORE anything else, so an
      //! in-flight hydration REST call (below) that captured an earlier generation knows a fresher
      //! WS frame has since landed and must not overwrite it.
      positionWsGenRef.current.set(sii, (positionWsGenRef.current.get(sii) || 0) + 1);

      //--> Close detection — accept new payload `status:'CLOSED'` AND legacy
      //*   `qty === 0` fallback. Drops the position from positionsRef on close.
      if (position.status === 'CLOSED' || position.qty === 0 || position.quantity === 0) {
        //! WS `position.{sii}` topic delivers an ALGO-shaped sii (deriveInstrumentId below only
        //! parses the 5-part ALGO shape) — scope the delete to the ALGO leg so a same-sii BD/MANUAL
        //! row is never collaterally removed by a WS close.
        positionsRef.current.deleteBySiiAndLeg(sii, 'ALGO');
      } else {
        const instrumentId = deriveInstrumentId(sii);
        //! Resolve the existing ALGO-leg entry (preserves any account_id a prior reconcile wrote),
        //! spread the WS payload on top, re-inject the identity axes, and upsert through the registry.
        const existing = positionsRef.current.getBySiiAndLeg(sii, 'ALGO')[0];
        positionsRef.current.upsert({
          ...existing,
          ...position,
          strategy_instance_id: sii,
          leg: 'ALGO',
          instrument_id: instrumentId,
        });
      }
      bumpVersion('positions');
      syncTickSubscriptions();
      syncLevelHistorySubscriptions();
    });
    //! Capture the SUBSCRIPTION ENTRY OBJECT (not just the sii string) so the hydration below can
    //! check IDENTITY, not just presence. Presence alone (`.has(sii)`) is not enough: an
    //! unpin -> re-pin cycle creates a BRAND NEW entry for the same sii, and REST fetch A (from
    //! the first pin) can resolve AFTER REST fetch B (from the second pin, e.g. a FLAT snapshot
    //! that already deleted the row) with no WS frame ever arriving to bump the generation (a
    //! closed position never sends another WS frame) — a bare `.has()` would let stale fetch A
    //! win. Identity-checking against `entry` closes this REST-vs-REST race (44-2 review F1).
    const entry = { unsub, count: 1 };
    activePositionSubsRef.current.set(sii, entry);

    //--> 44-2: hydrate the OMS snapshot on the FIRST pin only (the `existing` early-return above
    //*   handles ref-counted re-pins — they never reach here, so no refetch on a second consumer).
    //*   Subscribe FIRST (above), THEN fetch — a WS frame that lands mid-request is never missed.
    const gen = positionWsGenRef.current.get(sii) || 0;
    const perfTokens = new Map([[sii, takePerformanceToken(sii)]]);
    AlgoDashboardService.getAlgoSummary([sii])
      .then((res) => {
        //! Skip if a fresher WS frame landed while the REST call was in flight (WS always wins —
        //! design point 3), or if THIS PIN's entry is no longer the live one for the sii — either
        //! it was released (unpinned, no active subscriber left to show it to) or replaced by a
        //! later re-pin's own entry (REST-vs-REST guard — see the comment at `entry` above).
        if (activePositionSubsRef.current.get(sii) !== entry) return;
        const summaryData = res?.data?.data || {};
        if ((positionWsGenRef.current.get(sii) || 0) !== gen) {
          //! Verify A-1 (10502-3): a position frame overtook this request, so the POSITION half of
          //! the snapshot is stale and is dropped (WS wins). The family PERFORMANCE row has its
          //! own generation token (perfTokens) and is still applied — otherwise the Monitoring
          //! card sat on '--' until the next close frame.
          const perf = summaryData?.[sii]?.performance;
          if (perf) {
            setFamilyPerformance(sii, perf, perfTokens.get(sii));
            bumpVersion('performance');
          }
          return;
        }
        applyAlgoSummary(summaryData, perfTokens);
        bumpVersion('positions');
        bumpVersion('performance');
        syncTickSubscriptions();
        syncLevelHistorySubscriptions();
      })
      .catch((err) => {
        //! Warn-only — never throw. The WS subscription registered above stays live; the pin will
        //! still get live updates, it just starts from "no snapshot" until the next WS frame.
        console.warn('[TradingDataContext] position hydration failed', sii, err);
      });
  }, [subscribeTopic, bumpVersion, applyAlgoSummary, takePerformanceToken, setFamilyPerformance]);

  const unsubscribePosition = useCallback((sii) => {
    if (!sii) return;
    const entry = activePositionSubsRef.current.get(sii);
    if (!entry) return;
    entry.count -= 1;
    if (entry.count <= 0) {
      try { entry.unsub(); } catch { /* already gone */ }
      activePositionSubsRef.current.delete(sii);
    }
  }, []);

  const subscribePerformance = useCallback((sii) => {
    if (!sii) return;
    const existing = activePerformanceSubsRef.current.get(sii);
    if (existing) { existing.count += 1; return; }

    const topic = `performance.${sii}`;
    //--> 10502-3: the `performance.<sii>` close frame is an INVALIDATION event only. The OMS
    //*   projector publishes it post-commit, so a refetch of /algo/summary sees the committed
    //*   family aggregate (all five figures from ONE authority). The per-trade payload is NOT
    //*   merged — a shallow merge left totals at stale REST values. No timer, no poller.
    //! `entry` is captured by the callback so the response check can verify IDENTITY (an
    //!  unpin -> re-pin creates a brand-new entry for the same sii) as well as generation.
    const entry = { unsub: null, count: 1 };
    entry.unsub = subscribeTopic(topic, (msg) => {
      const frame = msg?.d || msg?.data || msg;
      if (!frame) return;
      const gen = takePerformanceToken(sii);
      AlgoDashboardService.getAlgoSummary([sii])
        .then((res) => {
          //! Drop if a newer frame's refetch has since been issued, or if THIS subscription entry
          //! is no longer the live one for the sii (released, or replaced by a later re-pin).
          if ((performanceRefreshGenRef.current.get(sii) || 0) !== gen) return;
          if (activePerformanceSubsRef.current.get(sii) !== entry) return;
          const performance = res?.data?.data?.[sii]?.performance;
          if (!performance) return;
          //! Performance ONLY — must not route through applyAlgoSummary, which would also
          //! overwrite positionsRef with a snapshot possibly older than a newer position frame.
          setFamilyPerformance(sii, performance, gen);
          bumpVersion('performance');
        })
        .catch((err) => {
          //! Warn-only; last good snapshot is preserved. The next close frame (or the reconnect
          //! init) retries naturally.
          console.warn('[TradingDataContext] performance refetch failed', sii, err);
        });
    });
    activePerformanceSubsRef.current.set(sii, entry);
  }, [subscribeTopic, bumpVersion, setFamilyPerformance, takePerformanceToken]);

  const unsubscribePerformance = useCallback((sii) => {
    if (!sii) return;
    const entry = activePerformanceSubsRef.current.get(sii);
    if (!entry) return;
    entry.count -= 1;
    if (entry.count <= 0) {
      try { entry.unsub(); } catch { /* already gone */ }
      activePerformanceSubsRef.current.delete(sii);
    }
  }, []);

  // =========================================================================
  // Reconcile API — R102.5 / R104.4 / R104.9
  // =========================================================================

  //--> runReconcile: C4 — calls consolidated GET /orders/consolidated via gateway.
  //*   The OMS POST /orders/live/reconcile was removed in C1; reconcile is now
  //*   snapshot-based (re-pull from the broker-snapshot read view).
  //*
  //*   B4 FIX: now writes BOTH ordersRef AND positionsRef so the Positions tab
  //*   re-renders after a reconcile (previously only ordersRef was updated).
  //*
  //*   B6: accepts optional { from_dt, to_dt } to forward a date-window to the
  //*   consolidated GET (the ALGO leg on the server honours the window; the BD/WS
  //*   snapshot leg is unaffected server-side). Existing { force } param is kept.
  //*
  //*   The WS lane (user.{id}.orders) continues to patch ordersRef on top of this
  //*   snapshot — it is NOT touched here (monotonic-status guard stays intact).
  //! R102.11 — account_id is optional; null/undefined = all-accounts server-side fan-out.
  //  Omit the param entirely when unset so the gateway does not see an empty string.
  //
  //! C2 (fix/bd-trades-no-pair-no-pnl) — runReconcile now RETURNS a result contract
  //! `{ok, rateLimited, retry_after}` instead of resolving to undefined. Previously the
  //! sweep 429 was swallowed here (log-only) and the caller had no way to distinguish a
  //! rate-limited-but-served reconcile from a genuinely fresh one — Orders.jsx's
  //! "Reconcile now" button silently looked like a success on every rapid repeat click.
  //! Existing behavior is UNCHANGED: a 429 (or any other sweep error) still falls through
  //! to the GET below and serves the as-of snapshot — only the return value is new, so
  //! existing fire-and-forget callers (Orders.jsx:594/629 _fetchAndReplaceWindow /
  //! handleRefreshBroker) that ignore the return are unaffected.
  const runReconcile = useCallback(async ({ force = false, from_dt, to_dt, account_id } = {}) => {
    setReconcileError(false);
    let rateLimited = false;
    let retryAfter = null;
    try {
      console.log('[DIAG] runReconcile start', { force, from_dt, to_dt, account_id: account_id ?? null });

      // --> Build query params; omit undefined/null keys so gateway doesn't see empty strings
      const params = {};
      if (force) params.force = true;
      if (from_dt) params.from_dt = from_dt;
      if (to_dt) params.to_dt = to_dt;
      //! R102.11 — forward account_id when set; omit (server fans out to all accounts) when null
      if (account_id) params.account_id = account_id;

      // --> R114 — POST sweep first, then GET the (now-populated) view.
      //     Build sweep params separately: force is not meaningful server-side (server rate-limits
      //     per-user), so only forward account_id to scope the sweep.
      //! Multi-day history: forward the window to the BACKFILL sweep so the POST refresh pulls
      //  the requested ORDER/TRADE range from the broker (POSITIONS stay current). Without this
      //  the sweep would only backfill today and the windowed GET would find no historical rows.
      const sweepParams = {};
      if (account_id) sweepParams.account_id = account_id;
      if (from_dt) sweepParams.from_dt = from_dt;
      if (to_dt) sweepParams.to_dt = to_dt;

      //! #102 R22 — fire the REAL RiskReconciliationJob alongside the existing broker_orders
      //  sweep below (a DIFFERENT subsystem — order-view's own consolidated refresh does NOT
      //  invoke it). Only when a SINGLE account is selected: reconcile-now is account-scoped
      //  and must never guess an account for the "all accounts" view. Server-side debounced +
      //  tenant-guarded (403 if not owned) — never allowed to break the existing refresh flow.
      if (account_id) {
        try {
          console.log('[DIAG] runReconcile reconcile-now start', { account_id });
          await services.trading.reconcileNow(account_id);
          console.log('[DIAG] runReconcile reconcile-now accepted', { account_id });
        } catch (reconcileNowErr) {
          console.log('[DIAG] runReconcile reconcile-now failed (non-fatal, continuing to sweep)', {
            account_id,
            status: reconcileNowErr?.response?.status,
            message: reconcileNowErr?.message,
          });
        }
      } else {
        console.log('[DIAG] runReconcile reconcile-now skipped — no single account selected');
      }

      try {
        console.log('[DIAG] runReconcile POST sweep start', { sweepParams });
        await services.trading.refreshConsolidated(sweepParams);
        console.log('[DIAG] runReconcile POST sweep accepted (202)');
      } catch (sweepErr) {
        const status = sweepErr?.response?.status;
        if (status === 429) {
          // Rate-limited: server returned cached snapshot — proceed to GET, serve as-of view.
          rateLimited = true;
          //! F2 — the 429 body nests under .error (orders_consolidated.py:300-309), not the
          //  response root; a bare data.retry_after_seconds read always misses and falls
          //  through to the header/last-resort branches below.
          retryAfter = sweepErr?.response?.data?.error?.retry_after_seconds ?? null;
          if (retryAfter == null) {
            //! Fallback to a Retry-After response header if the body didn't carry one.
            //! Guard against a non-numeric header value producing NaN (109-F3).
            const headerVal = sweepErr?.response?.headers?.['retry-after'];
            const n = Number(headerVal);
            retryAfter = headerVal != null && Number.isFinite(n) && n > 0 ? n : null;
          }
          //! Retry-After is not CORS-exposed by the gateway — the header fallback above will
          //  almost always miss too. Last resort so the countdown always has a real number.
          retryAfter = retryAfter ?? 30;
          const asOfHint = sweepErr?.response?.data?.error?.broker_orders_as_of ?? null;
          console.log('[DIAG] runReconcile POST sweep 429 — rate-limited, proceeding to GET', {
            retry_after_seconds: retryAfter,
            broker_orders_as_of: asOfHint,
          });
        } else {
          // Other sweep errors (503, network, etc.) — log and continue to GET (degraded-but-readable).
          //! Never block the read path on a failed sweep.
          console.log('[DIAG] runReconcile POST sweep error (non-429), proceeding to GET anyway', {
            status: status ?? 'network',
            message: sweepErr?.message,
          });
        }
        // In all sweep-error cases: fall through to the GET below.
      }

      // --> GET the consolidated view (now populated by the sweep above, or cached on rate-limit).
      const res = await services.trading.getConsolidatedView(params);
      const payload = res?.data?.data || res?.data || {};
      const orders          = payload.orders       || [];
      const positions       = payload.positions    || [];
      const trades          = payload.trades       || [];
      const asOf            = payload.as_of        || new Date().toISOString();
      //* R106.7 — broker_status: [{broker, degraded, error, account_id, error_code}]; defaults to [] if absent
      const brokerStatusList = payload.broker_status || [];
      setBrokerStatus(brokerStatusList);
      //! R104.14 + D7/M1 — FOUR independent structured degraded flags; do NOT use string-matching
      setOmsDegraded(!!payload.oms_degraded);
      setOmsPositionsDegraded(!!payload.oms_positions_degraded);   // D7/M1 — independent, never folded
      setClassificationDegraded(!!payload.classification_degraded);
      setBrokerDegraded(!!payload.broker_degraded);
      setBrokerOrdersAsOf(payload.broker_orders_as_of ?? null);
      console.log('[DIAG] runReconcile structured flags', {
        oms_degraded: !!payload.oms_degraded,
        classification_degraded: !!payload.classification_degraded,
        broker_degraded: !!payload.broker_degraded,
        broker_orders_as_of: payload.broker_orders_as_of ?? null,
      });

      // --> Store full snapshot for OrderDetailDrawer sourcing (R104.6)
      reconcileSnapshotRef.current = { orders, positions, trades, as_of: asOf };

      // --> Merge orders into ordersRef (keyed by order_id || id)
      //     Patch reconcile_state onto existing entries; add broker_only rows wholesale.
      orders.forEach((row) => {
        const key = row.order_id || row.id;
        if (!key) return;
        const existing = ordersRef.current.get(key);
        if (existing) {
          // Patch fields from snapshot without clobbering WS-patched fields.
          //! R104.13 guard: if the REST snapshot row carries a null/undefined reconcile_state
          //  (e.g. server returned null for this row), preserve the existing value that a prior
          //  WS delta or previous reconcile set. null ?? existing.reconcile_state keeps the good value.
          ordersRef.current.set(key, {
            ...existing,
            reconcile_state: row.reconcile_state ?? existing.reconcile_state,
          });
        } else {
          //! broker_only rows (arrive via reconcile, never via ALGO WS lane) — add them
          //*   so the operator can see them. Pre-parse _epoch from created_at if present.
          const epochMs = row.created_at
            ? new Date(row.created_at).getTime()
            : (row.updated_at ? new Date(row.updated_at).getTime() : Date.now());
          ordersRef.current.set(key, { ...row, _epoch: epochMs });
        }
      });

      // --> B4 FIX: merge positions into positionsRef via the registry (full-identity keyed).
      //     The registry's derivePositionRowKey handles ALGO (sii-keyed) and BD (broker+instrument
      //     composite) with no cross-leg collision. Epoch-stamp NEW entries before upsert so the
      //     existing _epoch semantics are preserved (existing rows keep their original _epoch).
      positions.forEach((pos) => {
        const key = derivePositionRowKey(pos);
        if (!key) return;
        const existing = positionsRef.current.getByKey(key);
        if (existing) {
          positionsRef.current.upsert(pos);   // merges { ...existing, ...pos }; keeps existing._epoch
        } else {
          const epochMs = pos.created_at
            ? new Date(pos.created_at).getTime()
            : Date.now();
          positionsRef.current.upsert({ ...pos, _epoch: epochMs });
        }
      });

      setReconcileAsOf(asOf);
      // --> Bump reconcile + orders + positions so both tabs re-render (B4)
      setVersions((prev) => ({
        ...prev,
        reconcile:  prev.reconcile  + 1,
        orders:     prev.orders     + 1,
        positions:  prev.positions  + 1,
      }));
      console.log('[DIAG] runReconcile complete', {
        orderCount:       orders.length,
        positionCount:    positions.length,
        tradeCount:       trades.length,
        brokerStatusCount: brokerStatusList.length,
        asOf,
      });
      //! C2 — contract return: the GET succeeded (readable), regardless of whether the
      //! sweep itself was rate-limited (rateLimited surfaces that distinction separately).
      return { ok: true, rateLimited, retry_after: retryAfter };
    } catch (err) {
      console.log('[DIAG] runReconcile failed', err);
      console.error('[TradingDataContext] runReconcile failed:', err);
      setReconcileError(true);
      return { ok: false, rateLimited, retry_after: retryAfter };
    }
  }, []);

  //--> Fetches the user's active broker account id. Mirrors watchlist pattern
  //*   from frontend/src/pages/WatchList/WatchList.jsx:54-80. Null on any failure.
  const fetchActiveBrokerAccount = useCallback(async () => {
    try {
      const res = await services.brokerAccounts.getAccounts();
      const accounts = res?.data?.accounts || res?.data?.data?.accounts || [];
      const active = accounts.find((a) => a.is_active) || accounts[0];
      brokerAccountIdRef.current = active?.account_id || null;
    } catch (err) {
      console.warn('[TradingDataContext] fetchActiveBrokerAccount failed', err);
      brokerAccountIdRef.current = null;
    }
  }, []);

  //--> Batch-fetches previous-close for all running deployments' instruments.
  //*   Needs brokerAccountIdRef to be set, silently no-ops if account is null.
  const fetchPreviousCloses = useCallback(async () => {
    const accountId = brokerAccountIdRef.current;
    if (!accountId) return;

    const instruments = [];
    deploymentsRef.current.forEach((dep) => {
      if (dep.status !== 'running') return;
      const id = liveInstrumentIdOf(dep);
      if (!id || previousCloseRef.current.has(id)) return;
      instruments.push({
        instrument_id: id,
        exchange: dep.instrument_exchange,
        symbol: dep.instrument_symbol,
      });
    });
    if (instruments.length === 0) return;

    try {
      const res = await services.brokerAccounts.getPreviousClose(accountId, instruments);
      const prices = res?.data?.data?.prices || res?.data?.prices || {};
      //* Response keys are broker-formatted (e.g. "NSE:RELIANCE:EQ") — map by
      //* resolved_instrument_id using the same instrument_id we sent.
      instruments.forEach((inst) => {
        //--> Match priority:
        //*   1. exact instrument_id key (happens when backend normalized to UUID)
        //*   2. `${exchange}:${symbol}` prefix match (broker-formatted keys like "NSE:RELIANCE:EQ")
        //*   3. otherwise skip — never substring-match an empty symbol (cross-contamination risk)
        let price = null;
        if (prices[inst.instrument_id] != null) {
          price = prices[inst.instrument_id];
        } else if (inst.exchange && inst.symbol) {
          const prefix = `${inst.exchange}:${inst.symbol}:`;
          const matchedKey = Object.keys(prices).find((k) => k.startsWith(prefix));
          if (matchedKey) price = prices[matchedKey];
        }
        if (price != null) previousCloseRef.current.set(inst.instrument_id, Number(price));
      });
    } catch (err) {
      console.warn('[TradingDataContext] fetchPreviousCloses failed', err);
    }
  }, []);

  // =========================================================================
  // Initialization — parallel fetch on mount when connected + user.id
  // =========================================================================

  useEffect(() => {
    if (!isConnected || !user?.id) return;

    let cancelled = false;

    const initialize = async () => {
      setIsInitialLoading(true);
      setError(null);

      try {
        //--> 1. Parallel fetch: deployments + orders
        const [depRes, ordRes] = await Promise.all([
          AlgoDashboardService.getUserDeployments(true, days),
          OrdersService.getOrders({ limit: 100 }),
        ]);

        if (cancelled) return;

        const deployments = depRes.data?.data?.deployments || [];
        const orders = ordRes.data?.data?.orders || [];

        //--> 2. Populate deploymentsRef
        deploymentsRef.current.clear();
        clearAllRuntimeDeadlines();
        deployments.forEach((dep) => {
          deploymentsRef.current.set(dep.deployment_id || dep.id, dep);
        });

        //--> 2b. Seed runtime health through the SAME chokepoint the push uses. lastHeard =
        //*   receipt - server-computed age; an invalid/absent blob stays `runtime_health: null`
        //*   (label: "Waiting for worker"). No refetch/poll — this is the only seed.
        const receiptMs = Date.now();
        deployments.forEach((dep) => {
          if (dep.status !== 'running') return;
          const depId = dep.deployment_id || dep.id;
          const age = dep.runtime_health_age_ms;
          const seeded = Number.isFinite(age)
            && applyRuntimeHealth(depId, dep.runtime_health, receiptMs - Math.max(0, age), { notify: false });
          if (!seeded) {
            deploymentsRef.current.set(depId, { ...dep, runtime_health: null, runtime_offline: false });
          }
        });

        //--> 3. Build strategy instance IDs from deployments
        const strategyInstanceIds = deployments
          .map((dep) => {
            const resolvedInstId = liveInstrumentIdOf(dep);
            return `${user.id}:${dep.strategy_id}:${dep.version_id}:${resolvedInstId || dep.instrument_id}:${dep.timeframe}`;
          })
          .filter(Boolean);

        //--> 4. Fetch OMS summary for positions + performance.
        //*   44-2 review F2: union with any sii ALREADY PINNED (activePositionSubsRef) — a
        //*   deep-dive/Monitoring tab pin's sii can be legitimately absent from
        //*   getUserDeployments (aged out of the `days` window, or a partial/paginated list) and,
        //*   because the pin is already ref-counted, subscribePosition's own first-pin hydration
        //*   never fires again to re-supply it. Without this union, a reconnect's clearLeg('ALGO')
        //*   below would silently drop that pinned position and never re-hydrate it.
        //*   Ordering note: on the PROVIDER's very first init run this union is always a no-op —
        //*   StrategyDeepDive derives its sii from `deploymentsRef` (populated by THIS effect),
        //*   and `versions.deployments` only bumps at the end of init, so no consumer can have
        //*   pinned a sii yet. The union only matters from the SECOND init run onward (reconnect —
        //*   isConnected false→true re-runs this effect with pins already live).
        const pinnedSiis = Array.from(activePositionSubsRef.current.keys());
        const summaryIds = Array.from(new Set([...strategyInstanceIds, ...pinnedSiis]));

        if (summaryIds.length > 0) {
          //! F-2: tokens taken BEFORE the request is sent (see takePerformanceToken).
          const perfTokens = new Map(summaryIds.map((id) => [id, takePerformanceToken(id)]));
          const summaryRes = await AlgoDashboardService.getAlgoSummary(summaryIds);
          if (cancelled) return;

          const summaryData = summaryRes.data?.data || {};

          //! Scoped clear — clears ONLY ALGO-leg rows so a concurrently-mounted Orders page's
          //! BD/MANUAL rows (written by runReconcile) survive this algo-summary refresh.
          positionsRef.current.clearLeg('ALGO');
          //! F-2: a sid whose token was overtaken (fresher close-frame/pin request) keeps its
          //!  fresher row across the clear; every other row is cleared as before.
          const keptRows = [];
          perfTokens.forEach((tok, id) => {
            if ((performanceRefreshGenRef.current.get(id) || 0) !== tok && performanceRef.current.has(id)) {
              keptRows.push([id, performanceRef.current.get(id)]);
            }
          });
          performanceRef.current.clear();
          keptRows.forEach(([id, row]) => performanceRef.current.set(id, row));
          //--> 44-2: shared with the first-pin hydration path — ONE application chokepoint.
          applyAlgoSummary(summaryData, perfTokens);
        }

        //--> 2026-04-19 — removed: per-strategy order pre-fetch
        //*
        //*   The previous per-SID pre-fetch (limit 200, no time window) leaked
        //*   orders from prior simulation runs into LiveAnalysisTab's
        //*   liveOrders stream. During an active sim that replayed a recent
        //*   historic window (e.g. Jan 5), orders from earlier runs covering
        //*   a different window (e.g. Jan 9) rode in alongside current-sim
        //*   orders. Both have valid bar_start, but the Jan 9 ones fall far
        //*   past the chart's visible range and lightweight-charts placed
        //*   their markers at extrapolated positions off the right edge —
        //*   the "markers piled to the right" symptom.
        //*
        //*   Why the removal is safe:
        //*   - useTradingData({strategyInstanceId}) had exactly one consumer
        //*     (LiveAnalysisTab) at the time of this removal, and that consumer
        //*     already fetches its own historical orders through a bar-clock-
        //*     windowed REST call (OrdersService.getOrders({fromBarStart,
        //*     toBarStart})) that mirrors useIndicatorHistory's pattern — i.e.
        //*     the fetch is attached to the chart's visible time window rather
        //*     than to a global unscoped limit.
        //*   - useTradingData() (no filter) is consumed by AlgoDashboard,
        //*     which still receives the global latest-100 fetch above plus
        //*     the WS subscription on user.{id}.orders for real-time events.
        //*
        //*   UPDATE 2026-07-21 (trades-view-strategy-backfill, issue #3) —
        //*   the "exactly one consumer" premise above is now FALSE:
        //*   StrategyDeepDive.jsx also calls useTradingData({strategyInstanceId})
        //*   (its Monitoring/Orders tabs), and unlike LiveAnalysisTab it has no
        //*   chart/time-window and did NOT do its own backfill — so the same
        //*   global-100 starvation this removal fixed for LiveAnalysisTab
        //*   resurfaced there as "No trades yet" despite an OPEN position.
        //*   Fixed WITHOUT reintroducing a per-SID fetch here (still the wrong
        //*   layer — no per-deployment time context): StrategyDeepDive now pulls
        //*   its own full-history backfill via useStrategyOrdersBackfill
        //*   (frontend/src/hooks/trading/useStrategyOrdersBackfill.js) and
        //*   merges it with this hook's scoped (WS-live) orders. Any FUTURE
        //*   consumer of the scoped hook must do the same — do not assume the
        //*   scoped hook alone is sufficient for a full trade history.
        //*
        //*   If AlgoDashboard ever needs guaranteed bracket-child coverage
        //*   for a long-running live session, the right place to add a
        //*   per-SID fetch is in AlgoDashboard itself with an appropriate
        //*   time window — NOT here, because this context has no per-
        //*   deployment time context.
        const mergedOrderMap = new Map();
        orders.forEach((order) => {
          const key = order.order_id || order.id;
          if (key) mergedOrderMap.set(key, order);
        });

        //--> Pre-parse _epoch on all orders
        ordersRef.current.clear();
        mergedOrderMap.forEach((order) => {
          //--> REST orders have created_at, WS events have timestamp
          order._epoch = new Date(order.timestamp || order.created_at || order.updated_at).getTime();
          ordersRef.current.set(order.order_id || order.id, order);
        });

        //--> 6. Increment all snapshot version counters after each re-initialization.
        //*   deploymentTicks and levelHistory which also live on this state
        //*   object. A plain replace here would make those keys `undefined`,
        //*   and the first bumpVersion would compute `undefined + 1 = NaN`,
        //*   after which `Object.is(NaN, NaN) === true` silently stops the
        //*   memo from recomputing — freezing the live-tick chain. Merge,
        //*   don't replace.)
        if (!cancelled) {
          setVersions((prev) => ({
            ...prev,
            orders: prev.orders + 1,
            positions: prev.positions + 1,
            performance: prev.performance + 1,
            deployments: prev.deployments + 1,
          }));
          setIsInitialLoading(false);

          //--> Sync tick subscriptions for open positions
          syncTickSubscriptions();

          //--> Sync trade_context subscriptions for any position that was ALREADY
          //*   open when this tab mounted. Without this, the subscription is only
          //*   set up on the next position WS update (line 656 below) — so opening
          //*   the Live Analysis tab on an existing open position would see zero
          //*   live SL/TP snapshots until the position state next changed. Mirror
          //*   the tick-sync pattern: build subscriptions from the AlgoSummary
          //*   seed immediately, then let the WS handler keep them in sync.
          syncLevelHistorySubscriptions();

          //--> Fire broker-account + previous-close fetch asynchronously.
          //*   Tick sub sync runs immediately — price badge streams first; change%
          //*   fills in once previous-close lands. Errors are handled inside each
          //*   helper (console.warn + null state), so this catch is belt-and-suspenders.
          fetchActiveBrokerAccount()
            .then(fetchPreviousCloses)
            .catch((err) => console.warn('[TradingDataContext] ticker init fetch failed', err));
          syncDeploymentTickSubscriptions();
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[TradingDataContext] Initialization failed:', err);
          setError(err.message || 'Failed to load trading data');
          setIsInitialLoading(false);
        }
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
  }, [isConnected, user?.id, days, syncTickSubscriptions, syncLevelHistorySubscriptions, syncDeploymentTickSubscriptions, fetchActiveBrokerAccount, fetchPreviousCloses, applyAlgoSummary, takePerformanceToken, applyRuntimeHealth, clearAllRuntimeDeadlines]);

  // =========================================================================
  // WebSocket subscriptions — 4 user topics
  // =========================================================================

  useEffect(() => {
    if (!isConnected || !user?.id) return;

    const userId = user.id;

    //--> 1. Orders topic
    const unsubOrders = subscribeTopic(`user.${userId}.orders`, (msg) => {
      const order = msg?.data || msg;
      if (!order) return;

      const orderId = order.order_id || order.id;
      if (!orderId) return;

      //--> Prefer the payload's explicit `status` (server truth — LV-M4, 2026-07-06);
      //    event_type mapping is only the fallback for payloads that lack it. The old
      //    unconditional overwrite turned unmapped events (ORDER_AMENDED) into UNKNOWN,
      //    hiding the status-gated Modify/Cancel actions until a REST refresh.
      if (!order.status && order.event_type) {
        order.status = mapEventTypeToStatus(order.event_type);
      }
      //--> LV-M5 (2026-07-06): the WS delta names the price `limit_price` while the
      //    consolidated REST row names it `price` — the additive merge kept the STALE
      //    `price`, which wins the renderer's `price ?? limit_price` precedence, so an
      //    AMENDED price never updated on screen. Normalize the delta before merging.
      if (order.price == null && (order.limit_price != null || order.trigger_price != null)) {
        order.price = order.limit_price ?? order.trigger_price;
      }
      //--> Pre-parse epoch
      order._epoch = order.timestamp ? new Date(order.timestamp).getTime() : Date.now();

      const existing = ordersRef.current.get(orderId);
      //! R104.13 — additive merge: WS delta is applied on top of existing row.
      //  Wholesale replacement (ordersRef[id] = delta) is FORBIDDEN — it would erase
      //  fields set by the REST reconcile snapshot (reconcile_state, brokerage, etc.).
      //  If the WS delta carries reconcile_state (server P9 stamps it for ALGO), accept it.
      //  If it does NOT carry reconcile_state, preserve the existing value via nullish
      //  coalescing so a WS update never silently resets classification.
      const merged = { ...existing, ...order };
      if (!Object.prototype.hasOwnProperty.call(order, 'reconcile_state') && existing?.reconcile_state != null) {
        merged.reconcile_state = existing.reconcile_state;
      }

      //--> R1.1-R1.3 (live-validation-fixups): derive the display-only Broker / Leg / Leg-type
      //    aliases from the WS frame when — and ONLY when — the existing row has no REST-supplied
      //    value yet. Mirrors the R104.13 reconcile_state guard ABOVE (checks `existing`, NOT the
      //    incoming payload's own key, per P29): WS order frames never carry broker/leg_type/leg
      //    keys at all, so an LV-M4/M5-style self-check (order.broker == null) would be true on
      //    EVERY frame and clobber the higher-fidelity REST value through `{...existing, ...order}`.
      //    Set-only-when-absent w.r.t. `existing` is what keeps "REST/existing wins" (R1.4).
      //! LIMITATION (live-validation-fixups C-6): once broker/leg/leg_type are set from a WS frame
      //!  they persist until a WHOLESALE store refresh (initial full-load fetch, or a remount that
      //!  replaces ordersRef). The incremental reconcile path in this file (the ordersRef.set at
      //!  ~L700-703) only ever upgrades reconcile_state — it NEVER re-derives these three fields.
      //!  Benign today: REST computes the SAME values it would upgrade to (broker_name is unpopulated
      //!  on every live row today, so REST's coalesce(broker_name, routed_broker_id) resolves to the
      //!  SAME routed_broker_id this alias already set; `source` is enum-driven identically on both
      //!  sides). If a future fix ever populates broker_name, a WS-born row's aliased `broker` could
      //!  silently diverge from a fresher REST broker_name until the next wholesale refresh —
      //!  surface THIS comment then and teach the incremental reconcile path to re-derive them too.
      if (existing?.broker == null && order.routed_broker_id != null) {
        merged.broker = order.routed_broker_id;   //! R1.1 — routed_broker_id is the canonical lowercase broker NAME (P2), the convention getBrokerBadge accepts natively
      }
      if (existing?.leg_type == null && order.action != null) {
        merged.leg_type = order.action;           //! R1.2
      }
      if (existing?.leg == null && order.source != null) {
        merged.leg = String(order.source).toUpperCase();   //! R1.3 — mirrors REST's OMS-row derivation leg=(source or "ALGO").upper() (consolidated_view.py:140)
      }

      //! Monotonic status guard — terminal statuses (FILLED/CANCELLED/REJECTED) must
      //! never be regressed by a later ORDER_CREATED (PENDING). The primary cause
      //! of this inversion (signal_consumer asyncio.gather on CREATED+FILLED) was
      //! fixed at the root in 2026-04-22 by backend staged publish
      //! (publish_staging.publish_staged). This guard remains as defense-in-depth
      //! against WS reconnect replay and Kafka rebalance re-delivery, which can
      //! still surface a stale ORDER_CREATED after an ORDER_FILLED has applied.
      const TERMINAL = new Set(['FILLED', 'CANCELLED', 'REJECTED']);
      if (existing?.status && TERMINAL.has(existing.status) && !TERMINAL.has(order.status)) {
        merged.status = existing.status;
        //*  Preserve fill-time-advanced fields. ORDER_CREATED carries the bracket-child's
        //*  inherited entry-bar bar_start (bracket_service.py:70) which is stale once the
        //*  bracket has actually fired (trigger_consumer.py:162 advances bar_start to the
        //*  breaching tick's ltp_time). A late CREATED would clobber the advanced value
        //*  and collapse the exit marker onto the entry candle. Same rationale for the
        //*  other fill-time fields.
        if (existing.filled_price != null) merged.filled_price = existing.filled_price;
        if (existing.filled_qty != null) merged.filled_qty = existing.filled_qty;
        if (existing.bar_start != null) merged.bar_start = existing.bar_start;
        if (existing.exit_reason != null) merged.exit_reason = existing.exit_reason;
        if (existing.slippage != null) merged.slippage = existing.slippage;
        if (existing.updated_at != null) merged.updated_at = existing.updated_at;
      }

      ordersRef.current.set(orderId, merged);
      bumpVersion('orders');
    });

    //--> 2. Positions topic
    const unsubDeployments = subscribeTopic(`user.${userId}.deployments`, (msg) => {
      const dep = msg?.data || msg;
      if (!dep) return;

      const depId = dep.deployment_id || dep.id;
      if (!depId) return;

      //--> Worker runtime health: an event-typed blob (NOT a status change) — store the raw blob and
      //*   re-arm this deployment's heartbeat deadline; never use the generic merge below.
      if (dep.event === 'runtime_health') {
        const { event: _event, deployment_id: _deploymentId, ...blob } = dep;
        applyRuntimeHealth(depId, blob, Date.now(), { notify: true });
        return;
      }

      if (dep.event === 'contract_rolled') {
        const existing = deploymentsRef.current.get(depId);
        //! Status is strategy-management's: a roll only re-points a deployment this page holds as running.
        if (!existing || existing.status !== 'running' || !dep.resolved_instrument_id) return;
        deploymentsRef.current.set(depId, {
          ...existing,
          resolved_instrument_id: dep.resolved_instrument_id,
          ...(dep.resolved_symbol ? { resolved_symbol: dep.resolved_symbol } : {}),
        });
        bumpVersion('deployments');
        fetchPreviousCloses();
        syncDeploymentTickSubscriptions();
        return;
      }

      //* Only track running/stopped deployments
      if (dep.status === 'running' || dep.status === 'stopped') {
        deploymentsRef.current.set(depId, {
          ...deploymentsRef.current.get(depId),
          ...dep,
          //--> A stopped deployment has no worker heartbeat: drop its deadline and runtime state.
          ...(dep.status === 'stopped' ? { runtime_health: null, runtime_offline: false } : {}),
        });
        if (dep.status === 'stopped') clearRuntimeDeadline(depId);
        bumpVersion('deployments');
        //--> NEW: refresh previous-close (new resolved_instrument_id may need it)
        //*   and re-sync tick subscriptions. fetchPreviousCloses is a no-op for
        //*   ids already in the map.
        fetchPreviousCloses();
        syncDeploymentTickSubscriptions();
      }
    });

    //--> Store unsubscribe functions for cleanup
    userSubsRef.current = [unsubOrders, unsubDeployments];

    return () => {
      //--> Unsubscribe all 4 user topics
      userSubsRef.current.forEach((unsub) => {
        if (typeof unsub === 'function') unsub();
      });
      userSubsRef.current = [];
    };
  }, [isConnected, user?.id, subscribeTopic, bumpVersion, syncTickSubscriptions, syncLevelHistorySubscriptions, syncDeploymentTickSubscriptions, fetchPreviousCloses, applyRuntimeHealth, clearRuntimeDeadline]);

  // =========================================================================
  // Cleanup on unmount — unsubscribe all tick topics, clear refs
  // =========================================================================

  useEffect(() => {
    return () => {
      //--> Unsubscribe all market tick topics
      activeTickSubsRef.current.forEach((unsubFn) => {
        if (typeof unsubFn === 'function') unsubFn();
      });
      activeTickSubsRef.current.clear();

      //--> Unsubscribe all trade-context topics (keep the history buffers so
      //*   a late consumer mount can still read; Provider unmount rarely happens
      //*   outside route teardown anyway).
      activeLevelHistorySubsRef.current.forEach((unsubFn) => {
        if (typeof unsubFn === 'function') unsubFn();
      });
      activeLevelHistorySubsRef.current.clear();

      //! User topic cleanup handled by the WS subscription effect (lines 325-331)
      //! — no need to double-unsubscribe here

      //--> Unsubscribe all deployment tick topics
      activeDeploymentTickSubsRef.current.forEach((unsub) => {
        if (typeof unsub === 'function') unsub();
      });
      activeDeploymentTickSubsRef.current.clear();

      //--> Unsubscribe all live-execution topics + drop cached payloads
      activeExecutionSubsRef.current.forEach((entry) => {
        try { entry.unsub(); } catch { /* already gone */ }
      });
      activeExecutionSubsRef.current.clear();
      latestByDeploymentIdRef.current.clear();

      //--> Clear every per-deployment runtime-health deadline
      runtimeDeadlinesRef.current.forEach((rec) => clearTimeout(rec.timeoutId));
      runtimeDeadlinesRef.current.clear();

      //--> Cancel any pending rAF to drop a queued version bump
      if (tickVersionRafRef.current != null) {
        cancelAnimationFrame(tickVersionRafRef.current);
        tickVersionRafRef.current = null;
      }

      //--> Clear tick data refs
      deploymentTicksRef.current.clear();
      previousCloseRef.current.clear();
      brokerAccountIdRef.current = null;

      //--> Clear all data refs
      ordersRef.current.clear();
      positionsRef.current.clear();
      performanceRef.current.clear();
      deploymentsRef.current.clear();
      ltpRef.current.clear();
      levelHistoryRef.current.clear();
      pnlListenersRef.current.clear();
    };
  }, []);

  // =========================================================================
  // Context value — memoized to prevent unnecessary consumer re-renders
  // =========================================================================

  const contextValue = useMemo(
    () => ({
      // Data refs (stable references — consumers read .current)
      ordersRef,
      positionsRef,
      performanceRef,
      deploymentsRef,
      ltpRef,
      levelHistoryRef,
      deploymentTicksRef,   // useDeploymentLtp reads tick state keyed by resolved_instrument_id
      latestByDeploymentIdRef, // useLiveExecution reads per-deployment node_results snapshot

      // Version counters (trigger selective re-renders)
      versions,

      // Loading / error state
      isInitialLoading,
      error,

      // Date range control
      days,
      setDays,

      // P&L listener API
      registerPnlListener,
      unregisterPnlListener,

      // Live strategy-execution subscription API (ref-counted, shared per deploymentId)
      subscribeLiveExecution,
      unsubscribeLiveExecution,

      // Per-SII position/performance subscription API (ref-counted)
      subscribePosition,
      unsubscribePosition,
      subscribePerformance,
      unsubscribePerformance,

      // Eager trade_context pin API (ref-counted per sii)
      ensureLevelHistorySubscription,
      releaseLevelHistorySubscription,

      // R102.5 / R104.4 reconcile API
      reconcileSnapshotRef,  // page-level snapshot ref for OrderDetailDrawer sourcing (R104.6)
      runReconcile,
      reconcileAsOf,
      reconcileError,
      brokerStatus,          // R106.7 — per-broker degraded flags [{broker, degraded, error, error_code}]
      //! R104.14 + D7/M1 — structured degraded flags (FOUR independent banners in Orders.jsx)
      omsDegraded,
      omsPositionsDegraded,
      classificationDegraded,
      brokerDegraded,
      brokerOrdersAsOf,

      // WS connection status — exposed so Orders.jsx live-stream indicator can react
      isConnected,
    }),
    [
      versions,
      isInitialLoading,
      error,
      days,
      registerPnlListener,
      unregisterPnlListener,
      subscribeLiveExecution,
      unsubscribeLiveExecution,
      subscribePosition,
      unsubscribePosition,
      subscribePerformance,
      unsubscribePerformance,
      ensureLevelHistorySubscription,
      releaseLevelHistorySubscription,
      runReconcile,
      reconcileAsOf,
      reconcileError,
      brokerStatus,
      omsDegraded,
      omsPositionsDegraded,
      classificationDegraded,
      brokerDegraded,
      brokerOrdersAsOf,
      isConnected,
    ],
  );

  return (
    <TradingDataContext.Provider value={contextValue}>
      {children}
    </TradingDataContext.Provider>
  );
}

// =============================================================================
// useTradingData — filtered access to trading data with version-gated memos
// =============================================================================

/**
 * Hook for consuming trading data with optional filtering.
 * @param {{ strategyInstanceId?: string }} filter
 */
export function useTradingData(filter = {}) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    throw new Error('useTradingData must be used within a TradingDataProvider');
  }

  const {
    ordersRef,
    positionsRef,
    performanceRef,
    deploymentsRef,
    versions,
    isInitialLoading,
    error,
    days,
    setDays,
  } = ctx;

  const sid = filter.strategyInstanceId;

  //--> Memoized arrays — recomputed only when the specific version counter bumps
  const orders = useMemo(() => {
    const arr = Array.from(ordersRef.current.values());
    if (sid) return arr.filter((o) => o.strategy_instance_id === sid);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.orders, sid]);

  const positions = useMemo(() => {
    const arr = Array.from(positionsRef.current.values());
    //! D10 — the SCOPED hook (useTradingData({strategyInstanceId})) is only ever called with an ALGO
    //  5-part sid (deployment surfaces), so the sid-filtered branch also narrows to leg==='ALGO'.
    //  This keeps AlgoTrading surfaces (StrategyDeepDive/MonitoringTab) from ever rendering a
    //  broker-truth (BD) row, and makes MonitoringTab's positions[0] safe (<=1 row post-filter).
    if (sid) return arr.filter((p) => p.strategy_instance_id === sid && (p.leg || 'BD') === 'ALGO');
    return arr;   // general, ALL-legs selector for the Trades page — unchanged
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.positions, sid]);

  const performance = useMemo(() => {
    const arr = Array.from(performanceRef.current.values());
    if (sid) return arr.filter((p) => p.strategy_instance_id === sid);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.performance, sid]);

  const deployments = useMemo(() => {
    const arr = Array.from(deploymentsRef.current.values());
    if (sid) {
      return arr.filter(
        (d) => d.strategy_instance_id === sid || d.deployment_id === sid,
      );
    }
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versions.deployments, sid]);

  return {
    orders,
    positions,
    performance,
    deployments,
    isInitialLoading,
    error,
    days,
    setDays,
  };
}

// =============================================================================
// useLivePnL — real-time unrealized P&L for a single strategy instance
// =============================================================================

/**
 * Hook for live unrealized P&L computation, throttled to 200ms (max 5 updates/sec).
 * @param {string} strategyInstanceId
 * @returns {{ unrealizedPnl: number, ltp: number|null }}
 */
export function useLivePnL(strategyInstanceId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    throw new Error('useLivePnL must be used within a TradingDataProvider');
  }

  const { positionsRef, ltpRef, registerPnlListener, unregisterPnlListener, versions } = ctx;

  const [pnlState, setPnlState] = useState({ unrealizedPnl: 0, ltp: null });

  // --> Throttle ref to enforce max 5 updates/sec
  const throttleRef = useRef(null);
  const pendingLtpRef = useRef(null);

  //! versions.positions in deps ensures effect re-runs when position opens/closes
  useEffect(() => {
    if (!strategyInstanceId) return;

    //! useLivePnL's public contract takes a bare sii; resolve the ALGO-leg row through the
    //! registry (correct for 100% of today's real callers — ALGO sii's are instrument-unique).
    const position = positionsRef.current.getBySiiAndLeg(strategyInstanceId, 'ALGO')[0];
    if (!position || !position.instrument_id) {
      //--> Position closed or doesn't exist — reset to zero
      setPnlState({ unrealizedPnl: 0, ltp: null });
      return;
    }

    const instrumentId = position.instrument_id;

    //--> Compute P&L from a given LTP
    const computePnl = (ltp) => {
      const pos = positionsRef.current.getBySiiAndLeg(strategyInstanceId, 'ALGO')[0];
      //! Zero/absent LTP defense-in-depth — never compute P&L off a null/undefined
      //! or <= 0 ltp (would render a false ₹0.00 unrealized P&L).
      if (!pos || ltp == null || ltp <= 0) return;

      const avgEntry = pos.avg_entry_price || pos.entry_price || 0;
      const qty = pos.qty || pos.quantity || 0;
      const multiplier = pos.contract_multiplier || 1;
      const sideMultiplier = (pos.side === 'SELL' || pos.side === 'SHORT') ? -1 : 1;

      const unrealizedPnl = (ltp - avgEntry) * qty * multiplier * sideMultiplier;
      setPnlState({ unrealizedPnl, ltp });
    };

    //--> Throttled tick callback (200ms)
    const tickCallback = (ltp) => {
      pendingLtpRef.current = ltp;

      if (!throttleRef.current) {
        throttleRef.current = setTimeout(() => {
          throttleRef.current = null;
          if (pendingLtpRef.current != null) {
            computePnl(pendingLtpRef.current);
          }
        }, 200);
      }
    };

    //--> Register listener
    registerPnlListener(instrumentId, tickCallback);

    //--> Initial computation if LTP already available
    const currentLtp = ltpRef.current.get(instrumentId);
    if (currentLtp != null) {
      computePnl(currentLtp);
    }

    return () => {
      //--> Cleanup: unregister listener + clear throttle timeout
      unregisterPnlListener(instrumentId, tickCallback);
      if (throttleRef.current) {
        clearTimeout(throttleRef.current);
        throttleRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyInstanceId, versions.positions, registerPnlListener, unregisterPnlListener]);

  return pnlState;
}

// =============================================================================
// useLevelHistory — per-bar SL/TP snapshot stream for a strategy instance
// =============================================================================

/**
 * Hook that returns the accumulated level_history for an open position's
 * strategy_instance_id. Each entry matches the backtest level_history shape:
 *   { timestamp, position: {side, entry_price, qty, entry_time}, levels: {stop_loss, take_profit, time_exit} }
 *
 * The array updates as new trade_context messages arrive over WebSocket.
 * Consumers that render the trade band overlay on the live/paper chart should
 * wrap this into the levelHistory array shape SmartChart expects (one trade
 * per entry, each with its own level_history sub-array).
 *
 * @param {string} strategyInstanceId
 * @returns {Array<Object>} level_history snapshots, newest last
 */
//--> Eager pin for trade_context subscription. Mount-time ref-counted hold so
//*   the WS topic is live BEFORE any OMS fill arrives — strategy-worker's
//*   entry-bar snapshot lands in the buffer instead of being dropped by the
//*   Redis → WS bridge. LiveAnalysisTab mounts with a known sii (derived from
//*   deployment + user.id), so the pin can be placed at tab-mount time.
export function useLevelHistoryPin(strategyInstanceId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    throw new Error('useLevelHistoryPin must be used within a TradingDataProvider');
  }
  const { ensureLevelHistorySubscription, releaseLevelHistorySubscription } = ctx;

  //! Stabilize via refs so the effect below only reacts to `sii` changes, not
  //!  to the Provider re-rendering on every version bump. Without this, every
  //!  snapshot-push bumps versions.levelHistory → Provider re-renders → useCallbacks
  //!  can get a new reference → this effect's deps change → cleanup (release) →
  //!  re-run (ensure) → syncLevelHistorySubscriptions drops+recreates the sub
  //!  → levelHistoryRef buffer gets RESET — every incoming snapshot wipes the
  //!  previous buffer content. Observed as "buffer never accumulates" in the
  //!  republish-dedup test; in the running app it would intermittently clear
  //!  the trade_context buffer under high-frequency streaming.
  const ensureRef = useRef(ensureLevelHistorySubscription);
  const releaseRef = useRef(releaseLevelHistorySubscription);
  ensureRef.current = ensureLevelHistorySubscription;
  releaseRef.current = releaseLevelHistorySubscription;

  useEffect(() => {
    if (!strategyInstanceId) return undefined;
    ensureRef.current(strategyInstanceId);
    return () => releaseRef.current(strategyInstanceId);
  }, [strategyInstanceId]);
}

//--> Eager pins for per-SII position/performance WS subscriptions (Spec 4).
//*   Same ref-stabilization pattern as useLevelHistoryPin — Provider re-renders
//*   on every version bump, so we stabilize the subscribe/unsubscribe fns via
//*   refs to keep this effect keyed only on `sii`.
export function usePositionPin(strategyInstanceId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    throw new Error('usePositionPin must be used within a TradingDataProvider');
  }
  const { subscribePosition, unsubscribePosition } = ctx;
  const subRef = useRef(subscribePosition);
  const unsubRef = useRef(unsubscribePosition);
  subRef.current = subscribePosition;
  unsubRef.current = unsubscribePosition;

  useEffect(() => {
    if (!strategyInstanceId) return undefined;
    subRef.current(strategyInstanceId);
    return () => unsubRef.current(strategyInstanceId);
  }, [strategyInstanceId]);
}

export function usePerformancePin(strategyInstanceId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    throw new Error('usePerformancePin must be used within a TradingDataProvider');
  }
  const { subscribePerformance, unsubscribePerformance } = ctx;
  const subRef = useRef(subscribePerformance);
  const unsubRef = useRef(unsubscribePerformance);
  subRef.current = subscribePerformance;
  unsubRef.current = unsubscribePerformance;

  useEffect(() => {
    if (!strategyInstanceId) return undefined;
    subRef.current(strategyInstanceId);
    return () => unsubRef.current(strategyInstanceId);
  }, [strategyInstanceId]);
}

export function useLevelHistory(strategyInstanceId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    throw new Error('useLevelHistory must be used within a TradingDataProvider');
  }

  const { levelHistoryRef, versions } = ctx;

  return useMemo(() => {
    if (!strategyInstanceId) return [];
    //! Spread to break reference-equality: the WS handler mutates the buffer in
    //! place (arr.push), so `.get(sii)` returns the SAME array instance across
    //! bumps. Downstream useMemo chains (buildCombinedLevelHistory → tradeSpanMap
    //! → TradeLevelOverlay setData) compare with Object.is — a stable ref freezes
    //! the live trade band after the first snapshot. A shallow copy per bump
    //! forces downstream recompute. O(N) with N capped at LEVEL_HISTORY_MAX=2000.
    const arr = levelHistoryRef.current.get(strategyInstanceId);
    return arr ? arr.slice() : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategyInstanceId, versions.levelHistory]);
}

// =============================================================================
// useDeploymentLtp — live price state for a single deployed strategy
// =============================================================================

/**
 * Returns render-ready ticker state for a deployment, or null if the deployment
 * isn't known yet. Subscribes transparently via the context's
 * syncDeploymentTickSubscriptions.
 *
 * Return shape:
 *   { ltp, change, changePct, direction, symbol, exchange, lastTickAt,
 *     isStale, isMarketOpen, state, resolvedInstrumentId }
 *   state ∈ { 'pending', 'live', 'stale', 'closed' }
 */
export function useDeploymentLtp(deploymentId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) throw new Error('useDeploymentLtp must be used within TradingDataProvider');
  const { deploymentsRef, deploymentTicksRef, versions } = ctx;

  return useMemo(() => {
    if (!deploymentId) return null;
    const dep = deploymentsRef.current.get(deploymentId);
    if (!dep) return null;

    const resolvedInstrumentId = liveInstrumentIdOf(dep);
    const tick = resolvedInstrumentId
      ? deploymentTicksRef.current.get(resolvedInstrumentId)
      : null;

    const ageMs = tick?.lastTickAt ? Date.now() - tick.lastTickAt : null;
    const isStale = ageMs != null && ageMs > 30_000;

    //! #31/exchange-session-ssot DK-4: isExchangeOpenNow is now tri-state
    //! (true / false / EXCHANGE_STATUS_UNKNOWN) — MDMS-fed, no static fallback.
    //! Only an EXPLICIT `false` may drive 'closed'; comparing with `===` rather
    //! than `!isMarketOpen` keeps EXCHANGE_STATUS_UNKNOWN out of that branch on
    //! purpose (today's fail-open guarantee: an unanswered question falls
    //! through to pending/stale/live, never a guessed 'closed').
    const isMarketOpen = isExchangeOpenNow(dep.instrument_exchange);

    //--> state precedence: closed (market hours) > pending (no tick yet)
    //*   > stale (tick but old) > live. Closed takes priority at the bell
    //*   so we never flicker stale → closed at 15:30.
    const state =
      isMarketOpen === false ? 'closed' :
      tick == null ? 'pending' :
      isStale ? 'stale' :
      'live';

    return {
      ltp: tick?.ltp ?? null,
      change: tick?.change ?? null,
      changePct: tick?.changePct ?? null,
      direction: tick?.direction ?? 'neutral',
      symbol: tick?.symbol ?? dep.instrument_symbol,
      exchange: tick?.exchange ?? dep.instrument_exchange,
      lastTickAt: tick?.lastTickAt ?? null,
      isStale,
      isMarketOpen,
      state,
      resolvedInstrumentId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId, versions.deploymentTicks, versions.deployments]);
}

// =============================================================================
// useLiveExecution — live strategy node_results + eval time for one deployment
// =============================================================================

/**
 * Subscribe to the strategy.execution.{deploymentId} WS topic via the
 * Provider's ref-counted dedup. Three components all calling
 * useLiveExecution(sameId) share ONE underlying subscription.
 *
 * Returns { liveNodeResults, lastEvalTime } — identical shape to the
 * (former) useStrategyExecution shim, deleted in 10502-5 — every consumer is on this hook.
 *
 * @param {string|null} deploymentId
 * @returns {{ liveNodeResults: object|null, lastEvalTime: string|null }}
 */
//--> Stable identity for "no node results yet": consumers key effects/memos on this object, so
//    it must never be a fresh `{}` per render (10502-5: a per-render `{}` in LiveAnalysisTab
//    re-ran the chart live-merge effect endlessly).
export const EMPTY_NODE_RESULTS = Object.freeze({});

export function useLiveExecution(deploymentId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) {
    throw new Error('useLiveExecution must be used within a TradingDataProvider');
  }

  const {
    latestByDeploymentIdRef,
    versions,
    subscribeLiveExecution,
    unsubscribeLiveExecution,
  } = ctx;

  //--> Mount/unmount subscription lifecycle. Ref-counted inside the Provider so
  //*   parallel consumers of the same deploymentId share one WS subscription.
  useEffect(() => {
    if (!deploymentId) return undefined;
    subscribeLiveExecution(deploymentId);
    return () => {
      unsubscribeLiveExecution(deploymentId);
    };
  }, [deploymentId, subscribeLiveExecution, unsubscribeLiveExecution]);

  return useMemo(() => {
    if (!deploymentId) {
      return { liveNodeResults: EMPTY_NODE_RESULTS, lastEvalTime: null, barTimeframe: null, engineDecision: null };
    }
    const entry = latestByDeploymentIdRef.current.get(deploymentId);
    return {
      liveNodeResults: entry?.nodeResults || EMPTY_NODE_RESULTS,
      lastEvalTime: entry?.lastEvalTime || null,
      //--> WS message's primary TF (= deployment timeframe). Used by chart
      //*  live-merge to gate against chart_tf when they differ.
      barTimeframe: entry?.barTimeframe || null,
      //! Board #73 — engine's real post-gate verdict, carried on the non-deprecated
      //!  hook (the former useStrategyExecution shim was deleted in 10502-5 once every consumer
      //!  patched in parallel. Same cache entry as liveNodeResults above.
      engineDecision: entry?.engineDecision || null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deploymentId, versions.liveExecution]);
}

export default TradingDataContext;
